import type { TFunction } from "i18next";
import type {
  AddStaffFormData,
  AddStaffValidationState,
} from "./addStaffPanel.contract";
import {
  getEmployeeNumberValidationError,
  getEmploymentDaysValidationError,
  getNameValidationError,
  getOptionalEmailValidationError,
  getOptionalPersonalIdentityNumberValidationError,
  getOptionalTextValidationError,
  getWorkPercentageValidationError,
  type StaffInputValidationError,
} from "../shared/staffInputValidation.logic";
import {
  toPaymentMethodValidationTranslationKey,
  validatePaymentMethodValue,
} from "../shared/staff.paymentMethod.logic";

export interface AddStaffFieldErrors {
  readonly agreement: string;
  readonly email: string;
  readonly employeeNumber: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly noticeDays: string;
  readonly paymentMethod: string;
  readonly personalIdentityNumber: string;
  readonly role: string;
  readonly vacationDays: string;
  readonly workPercentage: string;
  readonly workplace: string;
}

function getValidationErrorText({
  error,
  translate,
}: {
  readonly error: StaffInputValidationError | null;
  readonly translate: TFunction;
}): string {
  return error == null ? "" : translate(error.translationKey);
}

function getPaymentMethodError({
  formData,
  isValidPaymentMethod,
  translate,
}: {
  readonly formData: AddStaffFormData;
  readonly isValidPaymentMethod: boolean;
  readonly translate: TFunction;
}): string {
  if (isValidPaymentMethod) return "";
  const validationResult = validatePaymentMethodValue(
    formData.paymentMethodType,
    formData.paymentMethodValue,
  );
  if (validationResult.type === "success") return "";
  return translate(
    toPaymentMethodValidationTranslationKey(validationResult.error),
  );
}

export function getAddStaffFieldErrors({
  formData,
  translate,
  validationState,
}: {
  readonly formData: AddStaffFormData;
  readonly translate: TFunction;
  readonly validationState: AddStaffValidationState;
}): AddStaffFieldErrors {
  return {
    agreement: getValidationErrorText({
      error: getOptionalTextValidationError({
        translationKey: "Staff.AddStaffPanelAgreementTooLong",
        value: formData.agreement,
      }),
      translate,
    }),
    email: getValidationErrorText({
      error: getOptionalEmailValidationError({ value: formData.email }),
      translate,
    }),
    employeeNumber: getValidationErrorText({
      error: getEmployeeNumberValidationError({
        value: formData.employeeNumber,
      }),
      translate,
    }),
    firstName: getValidationErrorText({
      error: getNameValidationError({
        field: "firstName",
        value: formData.firstName,
      }),
      translate,
    }),
    lastName: getValidationErrorText({
      error: getNameValidationError({
        field: "lastName",
        value: formData.lastName,
      }),
      translate,
    }),
    noticeDays: getValidationErrorText({
      error: getEmploymentDaysValidationError({ value: formData.noticeDays }),
      translate,
    }),
    paymentMethod: getPaymentMethodError({
      formData,
      isValidPaymentMethod: validationState.hasValidPaymentMethod,
      translate,
    }),
    personalIdentityNumber: getValidationErrorText({
      error: getOptionalPersonalIdentityNumberValidationError({
        value: formData.personalIdentityNumber,
      }),
      translate,
    }),
    role: getValidationErrorText({
      error: getOptionalTextValidationError({
        translationKey: "Staff.AddStaffPanelRoleTooLong",
        value: formData.role,
      }),
      translate,
    }),
    vacationDays: getValidationErrorText({
      error: getEmploymentDaysValidationError({ value: formData.vacationDays }),
      translate,
    }),
    workPercentage: getValidationErrorText({
      error: getWorkPercentageValidationError({
        value: formData.workPercentage,
      }),
      translate,
    }),
    workplace: getValidationErrorText({
      error: getOptionalTextValidationError({
        translationKey: "Staff.AddStaffPanelWorkplaceTooLong",
        value: formData.workplace,
      }),
      translate,
    }),
  };
}
